import styled from "@emotion/styled";
import { PublicKey } from "@solana/web3.js";
import { formatBalance, URLIcon } from "@stepfinance/react";
import type { FC } from "react";

import { ReactComponent as MinusIcon } from "../../assets/minusIcon.svg";
import { ReactComponent as EqualIcon } from "../../assets/notifications/equalSign.svg";
import { ReactComponent as PlusIcon } from "../../assets/plusIcon.svg";
import { getTokenByMint } from "../../utils/tokenInfo";
import type { TokenIconProps } from "../TokenIcon";
import { TokenIcon } from "../TokenIcon";

type ChangeType = "increase" | "decrease" | "equal";

type TokenChangeNotificationDisplayProps = Pick<TokenIconProps, "mintAddress"> & {
  type: ChangeType;
  amount: number;
};

type NFTChangeDisplayProps = {
  type: ChangeType;
  amount: number;
  name: string;
  url: string;
};

type TokenChangeDisplayProps = {
  type: ChangeType;
  amount: number | string;
  name: string;
  icon: React.ReactNode;
};

const tokenChangeIcons = {
  increase: <PlusIcon />,
  decrease: <MinusIcon />,
  equal: <EqualIcon />,
};

export const TokenChangeNotificationDisplay: FC<TokenChangeNotificationDisplayProps> = ({
  type,
  mintAddress,
  amount,
}) => {
  const mintArray = (Array.isArray(mintAddress) ? mintAddress : [mintAddress ?? ""]).map((addr) =>
    addr instanceof PublicKey ? addr.toBase58() : addr,
  );
  const tokenAInfo = getTokenByMint(mintArray[0]);

  return (
    <TokenChangeDisplay
      type={type}
      amount={formatBalance(amount, tokenAInfo?.decimals ?? 6)}
      name={tokenAInfo?.symbol ?? ""}
      icon={<TokenIcon mintAddress={mintArray} size={28} />}
    />
  );
};

export const NFTChangeNotificationDisplay: FC<NFTChangeDisplayProps> = ({ type, name, url, amount }) => {
  return <TokenChangeDisplay amount={amount} icon={<URLIcon src={url} size={28} />} name={name} type={type} />;
};

const TokenChangeDisplay: FC<TokenChangeDisplayProps> = ({ type, amount, icon, name }) => {
  return (
    <TokenChangeDisplayContainer>
      <IconContainer>{tokenChangeIcons[type]}</IconContainer>
      <TokenAmountDisplayContainer>
        {icon}
        <TokenAmount>{amount}</TokenAmount>
        <TokenSymbol>{name}</TokenSymbol>
      </TokenAmountDisplayContainer>
    </TokenChangeDisplayContainer>
  );
};

const TokenAmountDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
`;

const TokenAmount = styled.span`
  font-family: Space Mono;
  margin-left: 10px;
`;

const TokenSymbol = styled.span`
  font-weight: 700 !important;
  margin-left: 10px;
`;

const TokenChangeDisplayContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  margin-left: 6px;
  display: flex;
  align-items: center;
`;
